import { apiRequest, buildUrl, getClientToken } from './client';
import {
  AdminUserDto,
  CompanyDto,
  CompanyUserDto,
  CreateAdminRequest,
  CreateCompanyRequest,
  CreateUserRequest,
  DataTableQuery,
  DataTableResponse,
  DesktopTokenDto,
  DesktopTokenVerifyResponse,
  IssuedDesktopTokenDto,
  LoginAsRequest,
  LoginAsResponse,
  OAuthCredentialSummaryDto,
  OAuthProviderType,
  ProcoreCompanyChoice,
  RemoveOAuthResponse,
  UpdateExtraBillingReceiversRequest,
  UpdatePbrRequest,
  UpdatePreferencesRequest,
  UpsertOAuthRequest,
  UserDto,
  UUID,
  WithProcoreCompanyResponse,
} from './types';

export const tenancyApi = {
  // --------------------------------------------------------------------------
  // Current User & Auth Session (`/ms-user/me`, `/ms-user/auth/*`)
  // --------------------------------------------------------------------------
  getMe(): Promise<UserDto> {
    return apiRequest<UserDto>('/ms-user/me', { scope: 'client' });
  },

  userLogout(): Promise<void> {
    return apiRequest<void>('/ms-user/auth/user-logout', {
      method: 'POST',
      scope: 'client',
    });
  },

  adminLogout(): Promise<void> {
    return apiRequest<void>('/ms-user/auth/admin-logout', {
      method: 'POST',
      scope: 'admin',
    });
  },

  // --------------------------------------------------------------------------
  // OAuth Credentials (`/ms-user/auth/*`, `/ms-user/oauth/*`)
  // --------------------------------------------------------------------------
  getOAuthStartUrl(provider: OAuthProviderType | 'google'): string {
    const token = getClientToken();
    if (provider === 'google') {
      return buildUrl('/ms-user/auth/google');
    }
    return buildUrl(`/ms-user/auth/${provider}`, {
      token: token || undefined,
    });
  },

  removeOAuthProvider(provider: OAuthProviderType): Promise<RemoveOAuthResponse> {
    return apiRequest<RemoveOAuthResponse>(`/ms-user/auth/remove/${provider}`, {
      method: 'DELETE',
      scope: 'client',
    });
  },

  upsertOAuthCredential(
    provider: OAuthProviderType,
    body: UpsertOAuthRequest,
  ): Promise<OAuthCredentialSummaryDto> {
    return apiRequest<OAuthCredentialSummaryDto>(`/ms-user/oauth/${provider}`, {
      method: 'PUT',
      body,
      scope: 'client',
    });
  },

  async listProcoreCompanies(): Promise<ProcoreCompanyChoice[]> {
    const res = await apiRequest<{
      result?: ProcoreCompanyChoice[];
      companies?: ProcoreCompanyChoice[];
    }>('/ms-user/procore/companies', {
      scope: 'client',
    });
    const list = res.companies || res.result || [];
    return list.map((c) => ({
      id: c.id,
      procore_company_id: String(c.procore_company_id || c.id),
      company_id: c.company_id,
      name: c.name,
      is_active: c.is_active ?? true,
    }));
  },

  selectProcoreCompany(procoreCompanyId: string | number): Promise<WithProcoreCompanyResponse> {
    return apiRequest<WithProcoreCompanyResponse>('/ms-user/auth/withProcoreCompany', {
      method: 'POST',
      body: { procoreCompanyId: String(procoreCompanyId) },
      scope: 'client',
    });
  },

  // --------------------------------------------------------------------------
  // Users & Preferences (`/ms-user/users`, `/ms-user/update-user-preferences`)
  // --------------------------------------------------------------------------
  listUsers(companyId?: UUID): Promise<UserDto[]> {
    return apiRequest<UserDto[]>('/ms-user/users', {
      query: { company_id: companyId },
    });
  },

  getUser(userId: UUID): Promise<UserDto> {
    return apiRequest<UserDto>(`/ms-user/users/${userId}`);
  },

  createUser(body: CreateUserRequest): Promise<UserDto> {
    return apiRequest<UserDto>('/ms-user/users', {
      method: 'POST',
      body,
    });
  },

  updateMyPreferences(body: UpdatePreferencesRequest): Promise<UserDto> {
    return apiRequest<UserDto>('/ms-user/update-user-preferences', {
      method: 'POST',
      body,
      scope: 'client',
    });
  },

  updateUserPreferences(userId: UUID, body: UpdatePreferencesRequest): Promise<UserDto> {
    return apiRequest<UserDto>(`/ms-user/update-user-preferences/${userId}`, {
      method: 'PUT',
      body,
    });
  },

  getUsersDataTable(query: DataTableQuery = {}): Promise<DataTableResponse<UserDto>> {
    return apiRequest<DataTableResponse<UserDto>>('/ms-user/data-table', {
      query: {
        page: query.page ?? 0,
        page_size: query.page_size ?? 25,
        company_id: query.company_id,
        search: query.search,
        is_login_as_allowed: query.is_login_as_allowed,
      },
      scope: 'admin',
    });
  },

  // --------------------------------------------------------------------------
  // Companies & Team Management (`/ms-user/companies`, `/ms-user/get-company*`)
  // --------------------------------------------------------------------------
  listCompanies(): Promise<CompanyDto[]> {
    return apiRequest<CompanyDto[]>('/ms-user/companies');
  },

  getCompany(companyId?: UUID): Promise<CompanyDto> {
    if (companyId) {
      return apiRequest<CompanyDto>(`/ms-user/companies/${companyId}`);
    }
    return apiRequest<CompanyDto>('/ms-user/get-company');
  },

  createCompany(body: CreateCompanyRequest): Promise<CompanyDto> {
    return apiRequest<CompanyDto>('/ms-user/companies', {
      method: 'POST',
      body,
    });
  },

  getCompaniesDataTable(query: DataTableQuery = {}): Promise<DataTableResponse<CompanyDto>> {
    return apiRequest<DataTableResponse<CompanyDto>>('/ms-user/companies/data-table', {
      query: {
        page: query.page ?? 0,
        page_size: query.page_size ?? 25,
        search: query.search,
      },
      scope: 'admin',
    });
  },

  getCompanyUsers(companyId?: UUID): Promise<CompanyUserDto[]> {
    return apiRequest<CompanyUserDto[]>('/ms-user/get-company-users', {
      query: { company_id: companyId },
    });
  },

  getCompanyPbr(companyId?: UUID): Promise<UserDto | null> {
    return apiRequest<UserDto | null>('/ms-user/get-company-pbr', {
      query: { company_id: companyId },
    });
  },

  updateCompanyPbr(body: UpdatePbrRequest): Promise<CompanyDto> {
    return apiRequest<CompanyDto>('/ms-user/update-pbr', {
      method: 'POST',
      body,
    });
  },

  updateExtraBillingReceivers(body: UpdateExtraBillingReceiversRequest): Promise<CompanyDto> {
    return apiRequest<CompanyDto>('/ms-user/update-extra-billing-receivers', {
      method: 'POST',
      body,
    });
  },

  // --------------------------------------------------------------------------
  // Desktop Connector Tokens (`/ms-user/user-desktop-tokens`, `/ms-user/auth/desktop/check`)
  // --------------------------------------------------------------------------
  listDesktopTokens(): Promise<DesktopTokenDto[]> {
    return apiRequest<DesktopTokenDto[]>('/ms-user/user-desktop-tokens', {
      scope: 'client',
    });
  },

  createDesktopToken(): Promise<IssuedDesktopTokenDto> {
    return apiRequest<IssuedDesktopTokenDto>('/ms-user/user-desktop-tokens', {
      method: 'POST',
      scope: 'client',
    });
  },

  revokeDesktopToken(tokenId: UUID): Promise<void> {
    return apiRequest<void>(`/ms-user/user-desktop-tokens/${tokenId}`, {
      method: 'DELETE',
      scope: 'client',
    });
  },

  verifyDesktopToken(token: string): Promise<DesktopTokenVerifyResponse> {
    return apiRequest<DesktopTokenVerifyResponse>('/ms-user/auth/desktop/check', {
      method: 'POST',
      body: { token },
      scope: 'client',
    });
  },

  // --------------------------------------------------------------------------
  // Admin Users & Impersonation (`/ms-user/admins/*`, `/ms-user/login-as`)
  // --------------------------------------------------------------------------
  getAdminsDataTable(query: DataTableQuery = {}): Promise<DataTableResponse<AdminUserDto>> {
    return apiRequest<DataTableResponse<AdminUserDto>>('/ms-user/admins/data-table', {
      query: {
        page: query.page ?? 0,
        page_size: query.page_size ?? 25,
        search: query.search,
      },
      scope: 'admin',
    });
  },

  listAdmins(): Promise<AdminUserDto[]> {
    return apiRequest<AdminUserDto[]>('/ms-user/get-users-admin', {
      scope: 'admin',
    });
  },

  createAdmin(body: CreateAdminRequest): Promise<AdminUserDto> {
    return apiRequest<AdminUserDto>('/ms-user/create-user-admin', {
      method: 'POST',
      body,
      scope: 'admin',
    });
  },

  deleteAdmin(adminId: UUID): Promise<void> {
    return apiRequest<void>(`/ms-user/delete/admin/${adminId}`, {
      method: 'DELETE',
      scope: 'admin',
    });
  },

  loginAs(body: LoginAsRequest): Promise<LoginAsResponse> {
    return apiRequest<LoginAsResponse>('/ms-user/login-as', {
      method: 'POST',
      body,
      scope: 'admin',
    });
  },
};
